import { useState, useRef, useCallback, useEffect } from 'react'
import { Search, X, UserPlus, Loader2 } from 'lucide-react'
import { searchContacts, createContact } from '../../hooks/useContacts'
import CompanySearchOrCreate from './CompanySearchOrCreate'
import { fullName } from '../../lib/contactName'
import { useConnectionRecovered } from '../../hooks/useConnectionRecovered'

/**
 * Contact search field that also offers to create a new contact if not found.
 * When creating, shows a mini-form for key details (name, title, company, phone, email).
 *
 * Props:
 *   value          – { id, name } or null
 *   onChange       – (value: { id, name } | null) => void
 *   placeholder    – input placeholder text
 *   compact        – smaller variant for inline use
 *   initialQuery   – pre-fill the search box and run the search immediately
 *                    (used when the person's name is already known, e.g. a
 *                    meeting attendee who isn't in the OS yet)
 *   createDefaults – { email?, phone? } pre-filled into the create form
 */
export default function ContactSearchOrCreate({ value, onChange, placeholder = 'Search contacts…', compact = false, initialQuery = '', createDefaults = null }) {
  const [input, setInput] = useState(value?.name || initialQuery || '')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createForm, setCreateForm] = useState({ first_name: '', last_name: '', title: '', company: null, phone: '', email: '' })
  const [creating, setCreating] = useState(false)
  const pendingSelectRef = useRef(false)
  const timerRef = useRef(null)
  const userTypingRef = useRef(false)

  // Sync input when value prop changes externally
  useEffect(() => {
    if (value?.name && value.name !== input) {
      userTypingRef.current = false
      setInput(value.name)
      setResults([])
      pendingSelectRef.current = true
    }
  }, [value?.name])

  // Kick off the search for a pre-filled query so matches (and the create
  // option) appear without the user retyping the name.
  useEffect(() => {
    if (!value?.id && initialQuery.trim().length >= 2) {
      userTypingRef.current = true
      doSearch(initialQuery.trim())
    }
  }, []) // eslint-disable-line

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return }
    setSearching(true)
    setError(null)
    try {
      const r = await searchContacts(q)
      setResults(r)
    } catch (err) {
      console.error('ContactSearchOrCreate search error:', err)
      setError('Search failed')
      setResults([])
    } finally {
      setSearching(false)
    }
  }, [])

  // Re-run the current search when the connection recovers, so the dropdown
  // populates without the user re-typing.
  useConnectionRecovered(() => { if (input.trim().length >= 2) doSearch(input) })

  const handleInput = (val) => {
    userTypingRef.current = true
    setInput(val)
    setError(null)
    setShowCreateForm(false)
    if (!val.trim()) {
      onChange(null)
      setResults([])
      return
    }
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => doSearch(val), 250)
  }

  const handleSelect = (item) => {
    pendingSelectRef.current = true
    setResults([])
    setShowCreateForm(false)
    const name = fullName(item)
    setInput(name)
    onChange({ id: item.id, name })
  }

  const openCreateForm = () => {
    const trimmed = input.trim()
    const parts = trimmed.split(/\s+/)
    setCreateForm({
      first_name: parts[0] || '',
      last_name: parts.slice(1).join(' ') || '',
      title: '',
      company: null,
      phone: createDefaults?.phone || '',
      email: createDefaults?.email || '',
    })
    setShowCreateForm(true)
    setResults([])
  }

  const handleCreate = async () => {
    if (!createForm.first_name.trim()) return
    setCreating(true)
    setError(null)
    try {
      const payload = {
        first_name: createForm.first_name.trim(),
        last_name: createForm.last_name.trim(),
        entity_id: 'chatrone_us',
      }
      if (createForm.title.trim()) payload.title = createForm.title.trim()
      if (createForm.company?.id) payload.organization_id = createForm.company.id
      if (createForm.phone.trim()) payload.phones = [{ type: 'Cell', number: createForm.phone.trim() }]
      if (createForm.email.trim()) payload.emails = [{ type: 'Work', email: createForm.email.trim() }]

      const newContact = await createContact(payload)
      const name = fullName(newContact)
      setInput(name)
      setShowCreateForm(false)
      pendingSelectRef.current = true
      onChange({ id: newContact.id, name })
    } catch (err) {
      console.error('ContactSearchOrCreate create error:', err)
      setError('Failed to create contact')
    } finally {
      setCreating(false)
    }
  }

  const handleClear = () => {
    setInput('')
    setResults([])
    setError(null)
    setShowCreateForm(false)
    onChange(null)
  }

  const handleBlur = () => {
    setTimeout(() => {
      if (!showCreateForm) setResults([])
      if (pendingSelectRef.current) { pendingSelectRef.current = false; return }
    }, 250)
  }

  const inputClass = compact
    ? 'input-field text-xs pr-7'
    : 'input-field pr-8'

  const setField = (k, v) => setCreateForm(f => ({ ...f, [k]: v }))

  return (
    <div className="relative">
      <div className="relative">
        <input
          className={inputClass}
          value={input}
          onChange={e => handleInput(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
        />
        {value?.id ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-warm-400 hover:text-red-500 transition-colors"
          >
            <X size={compact ? 11 : 13} />
          </button>
        ) : (
          <Search size={compact ? 11 : 13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-warm-400 pointer-events-none" />
        )}
      </div>

      {/* Dropdown results */}
      {userTypingRef.current && !showCreateForm && (results.length > 0 || (input.trim().length >= 2 && !searching && !value?.id)) && (
        <div className="absolute top-full mt-1 w-full bg-white border border-warm-300 rounded-lg shadow-lg z-30 overflow-hidden max-h-52 overflow-y-auto">
          {results.map(item => (
            <button key={item.id} type="button"
              className="w-full text-left px-3 py-2 hover:bg-warm-50 text-sm text-warm-800 transition-colors"
              onMouseDown={() => handleSelect(item)}>
              {fullName(item)}
              {item.title && <span className="text-warm-400 ml-1.5">— {item.title}</span>}
            </button>
          ))}
          {input.trim().length >= 2 && !results.some(r =>
            fullName(r).toLowerCase() === input.trim().toLowerCase()
          ) && (
            <button
              type="button"
              onMouseDown={openCreateForm}
              className="w-full text-left px-3 py-2 text-sm text-navy-600 hover:bg-navy-50 transition-colors flex items-center gap-1.5 border-t border-warm-200"
            >
              <UserPlus size={12} />
              Create "{input.trim()}" as new contact
            </button>
          )}
        </div>
      )}

      {/* Mini create form */}
      {showCreateForm && (
        <div className="absolute top-full mt-1 w-80 bg-white border border-warm-300 rounded-xl shadow-xl z-40 p-4 space-y-2.5">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-xs font-semibold text-warm-700 flex items-center gap-1.5">
              <UserPlus size={12} /> New Contact
            </h4>
            <button onClick={() => setShowCreateForm(false)} className="text-warm-400 hover:text-warm-600">
              <X size={12} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-warm-400 uppercase font-semibold">First Name</label>
              <input className="input-field text-xs mt-0.5" value={createForm.first_name}
                onChange={e => setField('first_name', e.target.value)} autoFocus />
            </div>
            <div>
              <label className="text-[10px] text-warm-400 uppercase font-semibold">Last Name</label>
              <input className="input-field text-xs mt-0.5" value={createForm.last_name}
                onChange={e => setField('last_name', e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-warm-400 uppercase font-semibold">Title</label>
            <input className="input-field text-xs mt-0.5" value={createForm.title}
              onChange={e => setField('title', e.target.value)} placeholder="e.g. VP Development" />
          </div>
          <div>
            <label className="text-[10px] text-warm-400 uppercase font-semibold">Company</label>
            <div className="mt-0.5">
              <CompanySearchOrCreate
                value={createForm.company}
                onChange={v => setField('company', v)}
                placeholder="Search company..."
                compact
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-warm-400 uppercase font-semibold">Phone</label>
            <input className="input-field text-xs mt-0.5" value={createForm.phone}
              onChange={e => setField('phone', e.target.value)} placeholder="(555) 123-4567" />
          </div>
          <div>
            <label className="text-[10px] text-warm-400 uppercase font-semibold">Email</label>
            <input className="input-field text-xs mt-0.5" value={createForm.email}
              onChange={e => setField('email', e.target.value)} placeholder="name@company.com" />
          </div>
          {error && <p className="text-[10px] text-red-500">{error}</p>}
          <button onClick={handleCreate} disabled={!createForm.first_name.trim() || creating}
            className="btn-primary w-full justify-center text-xs disabled:opacity-50">
            {creating
              ? <><Loader2 size={11} className="animate-spin" /> Creating...</>
              : <><UserPlus size={11} /> Create Contact</>}
          </button>
        </div>
      )}

      {/* Status indicators */}
      {searching && (
        <p className="text-[10px] text-warm-400 mt-0.5">Searching…</p>
      )}
      {error && !showCreateForm && (
        <p className="text-[10px] text-red-500 mt-0.5">{error}</p>
      )}
      {value?.id && !searching && !compact && (
        <p className="text-[10px] text-navy-500 mt-0.5">Linked to contact</p>
      )}
    </div>
  )
}
